import * as React from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  FormHelperText,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { fetchallpermisions } from "../../DAL/fetch";
import { createnewAdmin } from "../../DAL/create";
import FullPageLoader from "../../Components/FullPageLoader";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { enqueueSnackbar } from "notistack";

export default function AddAdmin() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ name: "", email: "", password: "" });
  const [allpermissions, setAllPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [error, setError] = useState([]);
  const [type, setType] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const permissionsResponse = await fetchallpermisions();
        if (permissionsResponse && permissionsResponse.data) {
          setAllPermissions(permissionsResponse.data || []);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleToggle = (item) => {
    setSelectedPermissions(
      (prevSelected) =>
        prevSelected.some((selectedItem) => selectedItem.id === item.id)
          ? prevSelected.filter((selectedItem) => selectedItem.id !== item.id) // Unselect
          : [...prevSelected, item] // Select
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const updateAdminDetails = async () => {
    setType(true);
    setLoading(true);
    const selectedIDS = selectedPermissions.map((item) => item.id);
    try {
      const response = await createnewAdmin({
        name: data.name.trim(),
        email: data.email.trim(),
        password: data.password.trim(),
        permissionIds: selectedIDS || "",
      });
      if (response?.status == 201) {
        enqueueSnackbar("Admin created successfully", { variant: "success" });
        navigate("/admins");
      } else {
        if (typeof response?.message == "string") {
          enqueueSnackbar(response?.message[0]?.constraints, {
            variant: "error",
          });
        } else {
          enqueueSnackbar(response?.message[0]?.constraints, {
            variant: "error",
          });
        }
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const labelStyles = {
    color: "", // Default label color
    "&.Mui-focused": {
      color: "#8F29D0", // Focused label color (green)
    },
  };
  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": {
        borderColor: "#8F29D0", // Hover border color
      },
      "&.Mui-focused fieldset": {
        borderColor: "#8F29D0", // Focus border color (green)
      },
    },
  };

  return (
    <Box sx={{ padding: "3%" }}>
      {loading ? (
        <FullPageLoader />
      ) : (
        <>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <ArrowBackIcon
              sx={{ cursor: "pointer" }}
              onClick={() => navigate(-1)}
            />
            <Typography id="modal-title" variant="h5" component="h2">
              Add Sub Admin
            </Typography>
          </Box>
          <Box
            component="form"
            sx={{
              mt: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
            }}
          >
            <Grid container spacing={2}>
              <Grid item lg={6}>
                <TextField
                  fullWidth
                  label="Name *"
                  variant="outlined"
                  value={data.name || ""}
                  size="small"
                  name="name"
                  onChange={handleInputChange}
                  sx={inputStyles}
                  InputLabelProps={{ sx: labelStyles }}
                  inputProps={{ maxLength: 22 }}
                />
                <FormHelperText sx={{ color: "red" }}>
                  {type == true && !data?.name && "Name field is required"}
                </FormHelperText>
              </Grid>
              <Grid item lg={6}>
                <TextField
                  fullWidth
                  label="Email *"
                  variant="outlined"
                  size="small"
                  value={data.email || ""}
                  name="email"
                  onChange={handleInputChange}
                  sx={inputStyles}
                  InputLabelProps={{ sx: labelStyles }}
                  inputProps={{ maxLength: 25 }}
                />
                <FormHelperText sx={{ color: "red" }}>
                  {type == true && !data?.email
                    ? "Email field is required"
                    : error[0]?.constraints}
                </FormHelperText>
              </Grid>
              <Grid item lg={6}>
                <TextField
                  fullWidth
                  label="Password *"
                  variant="outlined"
                  value={data.password || ""}
                  name="password"
                  size="small"
                  type={showPassword ? "text" : "password"}
                  onChange={handleInputChange}
                  sx={inputStyles}
                  InputLabelProps={{ sx: labelStyles }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <FormHelperText sx={{ color: "red" }}>
                  {type == true &&
                    !data?.password &&
                    "Password field is required"}
                </FormHelperText>
              </Grid>
            </Grid>

            <Typography variant="h6">Permissions *</Typography>
            <FormHelperText sx={{ color: "red" }}>
              {type == true &&
                selectedPermissions?.length == 0 &&
                "Please select atleast one permission"}
            </FormHelperText>
            <Box sx={{ mt: 2 }}>
              <Grid
                container
                spacing={2}
                sx={{
                  maxHeight: "50vh",
                  overflowY: "scroll",
                }}
              >
                {allpermissions.map((allpermission) => (
                  <Grid item xs={12} sm={6} md={4} key={allpermission.id}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedPermissions.some(
                            (selectedItem) =>
                              selectedItem.id === allpermission.id
                          )}
                          onChange={() => handleToggle(allpermission)}
                          sx={{
                            "&.Mui-checked": {
                              color: "#8F29D0",
                            },
                          }}
                        />
                      }
                      label={allpermission.name}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  type="button"
                  variant="contained"
                  sx={{ backgroundColor: "#B1B1B1", textTransform: "none" }}
                  onClick={() => {
                    navigate("/admins");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  className="gradientButton"
                  onClick={updateAdminDetails}
                  sx={{ textTransform: "none" }}
                >
                  Submit
                </Button>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
